/**
 * VerifyEmailPage
 * Destination of the "Verify my email" link in the registration email.
 * Reads ?token=XXX, hits GET /auth/verify-email, shows result, links to login.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components/ui';
import { api } from '../core/api-client';

type Phase = 'verifying' | 'success' | 'error';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [phase, setPhase] = useState<Phase>('verifying');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setPhase('error');
      setMessage(
        t('verifyEmail.missingToken', 'Missing verification token in the link.'),
      );
      return;
    }
    (async () => {
      try {
        const resp = await api.get<{ message: string }>('/auth/verify-email', {
          params: { token },
        });
        setPhase('success');
        setMessage(
          resp?.message ||
            t(
              'verifyEmail.success',
              'Email verified. You can now log in.',
            ),
        );
      } catch (e) {
        const err = e as {
          response?: { data?: { detail?: string; message?: string } };
        };
        setPhase('error');
        setMessage(
          err?.response?.data?.detail ||
            err?.response?.data?.message ||
            t(
              'verifyEmail.error',
              'Verification link is invalid or expired. Request a new one from the login page.',
            ),
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-[var(--surface-ground)] flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-sm sm:max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <img
            src="/logo.svg"
            alt="POLYREQ"
            className="mx-auto h-16 w-16 sm:h-20 sm:w-20 object-contain mb-4"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)]">
            {t('verifyEmail.title', 'Email verification')}
          </h1>
        </div>

        <Card>
          <div className="p-5 sm:p-6 text-center space-y-4">
            {phase === 'verifying' && (
              <>
                <div className="mx-auto w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-[var(--text-secondary)]">
                  {t('verifyEmail.inProgress', 'Verifying your email…')}
                </p>
              </>
            )}
            {phase === 'success' && (
              <>
                <div className="mx-auto w-12 h-12 rounded-full bg-green-600/15 text-green-600 flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm text-[var(--text-primary)]">{message}</p>
                <Button onClick={() => navigate('/user-login')} className="w-full">
                  {t('verifyEmail.goToLogin', 'Go to login')}
                </Button>
              </>
            )}
            {phase === 'error' && (
              <>
                <div className="mx-auto w-12 h-12 rounded-full bg-red-600/15 text-red-600 flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <p className="text-sm text-[var(--text-primary)]">{message}</p>
                <Button onClick={() => navigate('/user-login')} className="w-full">
                  {t('verifyEmail.backToLogin', 'Back to login')}
                </Button>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default VerifyEmailPage;
